import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { LevelBadge, PremiumBadge } from "@/components/Badges";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle } from "lucide-react";
import { ProfilTabs } from "@/components/TalentSectionTabs";
import { TALENT_TYPES } from "@/lib/roleCategories";

export default function TalentProfileEdit() {
  const { user, profile, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    bio: "",
    location: "",
    experience: "",
    talent_type: "",
    available: true,
    height: "", measurements: "", shoe_size: "", hair_color: "", eye_color: "",
  });

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "talent") navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (profile) {
      setForm({
        bio: profile.bio || "",
        location: profile.location || "",
        experience: profile.experience || "",
        talent_type: profile.talent_type || "",
        available: profile.available ?? true,
        height: profile.height || "", measurements: profile.measurements || "", shoe_size: profile.shoe_size || "",
        hair_color: profile.hair_color || "", eye_color: profile.eye_color || "",
      });
    }
  }, [profile]);

  const handleSave = async () => {
    await api.put("/talents/me", form);
    await checkAuth();
    toast.success("Profil mis à jour");
  };

  if (!user || !profile) return null;

  const checks = [
    { key: "bio", label: "Biographie", done: !!form.bio },
    { key: "location", label: "Localisation", done: !!form.location },
    { key: "experience", label: "Expérience", done: !!form.experience },
    { key: "talent_type", label: "Type de talent", done: !!form.talent_type },
    { key: "height", label: "Taille & mensurations", done: !!form.height && !!form.measurements },
    { key: "main", label: "Photo principale", done: !!profile.main_photo_id },
  ];

  const MEASURE_FIELDS = [
    { key: "height", label: "Taille", placeholder: "178 cm" },
    { key: "measurements", label: "Mensurations", placeholder: "86 / 62 / 90" },
    { key: "shoe_size", label: "Pointure", placeholder: "39" },
    { key: "hair_color", label: "Cheveux", placeholder: "Châtain" },
    { key: "eye_color", label: "Yeux", placeholder: "Marron" },
  ];

  return (
    <DashboardLayout>
      <ProfilTabs />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#EADDC9] p-8">
          <div className="flex items-center gap-3 mb-6">
            <LevelBadge level={profile.level} />
            {profile.is_premium && <PremiumBadge />}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="caption-label">Type de talent</Label>
              <Select value={form.talent_type} onValueChange={(v) => setForm({ ...form, talent_type: v })}>
                <SelectTrigger className="rounded-xl mt-2" data-testid="edit-talent-type">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {TALENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption-label">Localisation</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="rounded-xl mt-2"
                data-testid="edit-location"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="caption-label">Expérience</Label>
              <Input
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
                className="rounded-xl mt-2"
                data-testid="edit-experience"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="caption-label">Biographie</Label>
              <Textarea
                rows={5}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="rounded-xl mt-2"
                data-testid="edit-bio"
              />
            </div>
            <div className="sm:col-span-2 pt-4 mt-2 border-t border-[#EADDC9]" data-testid="measurements-section">
              <p className="caption-label text-[#C68E59]">Taille & mensurations</p>
              <p className="text-xs text-[#8B8680] mt-1">Informations essentielles pour les castings mode et beauté — visibles par les marques et créateurs.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                {MEASURE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="caption-label">{f.label}</Label>
                    <Input
                      value={form[f.key]}
                      placeholder={f.placeholder}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="rounded-xl mt-2"
                      data-testid={`edit-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between pt-2">
              <Label className="caption-label m-0">Disponible pour des projets</Label>
              <Switch
                checked={form.available}
                onCheckedChange={(v) => setForm({ ...form, available: v })}
              />
            </div>
          </div>

          <Button
            onClick={handleSave}
            className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white mt-6"
            data-testid="save-profile-btn"
          >
            Enregistrer les modifications
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-[#EADDC9] p-6 h-fit">
          <p className="caption-label text-[#8B8680]">Progression du profil</p>
          <div className="flex items-center justify-between mt-2 mb-3">
            <span className="font-serif text-4xl text-[#1A1A1A]">{profile.progress}%</span>
            <LevelBadge level={profile.level} />
          </div>
          <Progress value={profile.progress} className="h-1.5" />

          <div className="mt-6 space-y-3">
            {checks.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm">
                {c.done ? (
                  <CheckCircle2 className="w-4 h-4 text-[#2E7D32]" />
                ) : (
                  <Circle className="w-4 h-4 text-[#C4C4C4]" />
                )}
                <span className={c.done ? "text-[#1A1A1A]" : "text-[#8B8680]"}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
